import { ArrowDownTrayIcon, BanknotesIcon, CheckCircleIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { InputType } from 'config'
import { useSharedLoan } from 'context/SharedLoanContext'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  approvePayoffStatement,
  deletePayoffStatement,
  downloadCombinedPayoff,
  downloadFile,
  downloadPayoff,
  downloadPayoffStatement,
  getPayoffLineItems,
  getPayoffStatements,
  getServicingNotes,
  getTrustLedgerAdditionalInfo,
  recordPartialRelease,
  updateTrustLedgerAdditional,
} from 'services'
import { Button } from 'stories/components'
import {
  borrowerRecipients,
  confirm,
  formatDateYMD,
  formatTime,
  getPrice2decimal,
  InputConvert,
  InputValidate,
  openAuditLog,
  removeComma,
} from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import CollateralPanel, { CollateralRow } from './CollateralPanel'
import CombinedPayoffPanel from './CombinedPayoffPanel'
import { defaultInputs } from './constants'
import { lastQuotedDate } from './lastQuotedDate'

// Generate controls that drive the PDF but aren't persisted on the loan record.
const generateInputs = (): Record<string, InputType> => ({
  sendEmail: {
    inputType: 'select',
    title: 'Action',
    required: true,
    value: 'download',
    options: {
      download: 'Download PDF',
      email: 'Email PDF',
    },
  },
  emailTo: {
    inputType: 'text',
    title: 'Email to',
    required: false,
  },
})

// Line item as returned by the backend.
interface BackendLine {
  title: string
  value: string
  kind?: 'computed' | 'manual'
  optional?: boolean
}

// Line item as shown/edited on the page.
interface LineItem {
  title: string
  value: string
  /** Optional lines (e.g. the impounds credit) can be toggled off before generating. */
  optional?: boolean
  /** Whether an optional line is currently included in the payoff total/PDF. */
  included: boolean
  /** A line added from the category picker can be removed back into the picker. */
  isCategory?: boolean
}

/** A statement already generated on this loan, as the log lists it. */
interface PayoffStatementLog {
  id: number
  payoffDate: string
  totalAmount: number
  fileName: string
  emailedTo: string
  status: 'draft' | 'pending' | 'approved'
  createdBy: string
  createdAt: string
  approvedBy: string
  approvedAt: string | null
  /** Set when a borrower asked the servicer to issue a real statement off this draft. */
  requestedBy: string
  requestedAt: string | null
  requestNote: string
  requestFulfilled: boolean
  /** 'payoff' or 'partialRelease'. A partial release names one parcel and leaves the loan open. */
  variant: string
  collateralId: number | null
  releasePrincipal: number
  recastPayment: boolean
  /** Set once the money arrived and the lien was released; until then the demand is a promise. */
  releaseRecordedAt: string | null
  releasePaymentId: number | null
}

const TOTAL_TITLE = 'Amount to Fully Pay Off this Loan'
const PARTIAL_TOTAL_TITLE = 'Amount to Release this Collateral'
const PARTIAL = 'partialRelease'

const toNumber = (value: any) => {
  const n = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : Number(value)
  return isNaN(n) ? 0 : n
}

// Blank values render as $0 on the statement.
const displayValue = (value: string) => (value === '' || value == null ? '0' : value)

// Format a number as US currency for display, preserving a leading minus for credits.
const formatUSD = (n: number) =>
  `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export default function Payoff() {
  const shared = useSharedLoan()
  const isShared = !!shared?.readOnly
  const { hasPermission } = usePermissions()
  const [loading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>(defaultInputs())
  const [genInputs, setGenInputs] = useState<Record<string, InputType>>(generateInputs())
  // The loan's borrower, already loaded for the file. Same source the Servicing Documents send
  // dialog uses, so the address offered here is the one offered there.
  const { borrower } = useSelector((state: any) => ({ borrower: state.borrower?.borrower }))
  const [fieldValueChanged, setFieldValueChanged] = useState(false)
  const [servicingInfo, setServicingInfo] = useState<any>({})
  const [lineItems, setLineItems] = useState<LineItem[]>([])
  const [available, setAvailable] = useState<{ title: string; value: string }[]>([])
  const [statements, setStatements] = useState<PayoffStatementLog[]>([])
  // Partial payoff: which parcel is paying off, what is being taken for it, and whether the
  // scheduled payment is re-amortized on what is left.
  const [isPartial, setIsPartial] = useState(false)
  const [collateral, setCollateral] = useState<CollateralRow[]>([])
  const [collateralId, setCollateralId] = useState<number | null>(null)
  // Held as text, not a number, so clearing the field does not snap back to the default price.
  // `quotedPrincipal` is the value the quote has actually been requoted for: the field commits on
  // blur rather than on every keystroke, which would re-price the whole statement per character.
  const [releasePrincipal, setReleasePrincipal] = useState<string>('')
  const [quotedPrincipal, setQuotedPrincipal] = useState<string>('')
  const [recast, setRecast] = useState(false)
  // Combined payoff: the related loans this one demand also retires. A borrower paying off through
  // one closing pays one wire, so the loans are chosen here and quoted together.
  const [isCombined, setIsCombined] = useState(false)
  const [combinedLoans, setCombinedLoans] = useState<number[]>([])
  // Bumped after a release posts, so the register pulls the newly released parcel through.
  const [collateralVersion, setCollateralVersion] = useState(0)
  const loanNumber = isShared ? shared.loanNumber : setLoanNumber()

  const payoffDate = inputs.payoffDate?.value
  // Only parcels that still secure the loan can pay off; a released one is already gone.
  const releasableCollateral = collateral.filter((row) => !`${row.releasedDate || ''}`.trim())
  const selectedCollateral = collateral.find((row) => row.id === collateralId) || null
  const totalTitle = isPartial ? PARTIAL_TOTAL_TITLE : TOTAL_TITLE

  // A statement with no maturity date or no rate on it is not a quote: the rate drives every
  // interest figure and the maturity date is what tells the reader whether the loan is still
  // running. Name what is missing and keep the button off rather than letting a click fail.
  const missingTerms = [
    servicingInfo?.curMatDate ? '' : 'Current Maturity Date',
    servicingInfo?.currentInterestRate ? '' : 'Current Interest Rate',
  ].filter(Boolean)
  const canApprove = hasPermission('Loan.Servicing.Payoff.Approval')
  // A user holding only the draft right - the broker-facing grant - can quote a figure but never
  // issue a demand, so the page collapses to the draft button alone. The endpoint forces the same
  // thing, since the client is exactly what such a user controls.
  const draftOnly = !hasPermission('Loan.Servicing.Payoff') && hasPermission('Loan.Servicing.Payoff.Draft')

  useEffect(() => {
    if (isShared) return
    setLoading(true)
    init()
  }, [isShared])

  /**
   * Open the Email to field on the borrower's own addresses rather than empty, so issuing a demand
   * does not mean retyping addresses the file already holds.
   *
   * Every address the borrower keeps, not just the primary: a borrower who lists additional
   * addresses expects the demand at all of them, and offering only the first meant the rest were
   * quietly left off every payoff that went out.
   *
   * Only ever fills a field nobody has touched: a servicer who has typed a different recipient,
   * or cleared it deliberately, keeps what they typed even when the borrower record arrives late.
   */
  const borrowerEmails = borrowerRecipients(borrower).join(', ')
  useEffect(() => {
    if (isShared) return
    if (!borrowerEmails) return
    setGenInputs((prev) => {
      if (!prev.emailTo || prev.emailTo.value) return prev
      const next = cloneDeep(prev)
      next.emailTo.value = borrowerEmails
      return next
    })
  }, [isShared, borrowerEmails])

  const init = async () => {
    const data = await getTrustLedgerAdditionalInfo()
    const { data: servicingSummaryInfo } = await getServicingNotes()
    setServicingInfo(servicingSummaryInfo)
    const logged = await loadStatements()

    let newInputs = cloneDeep(inputs)
    Object.keys(newInputs).forEach((key) => {
      newInputs[key].value = data[key] || ''
    })

    // The screen opens ready to quote rather than blank. A date the servicer saved earlier wins,
    // because it is a deliberate choice and something they may be mid-way through; otherwise an
    // unanswered borrower request supplies the date they asked for, and failing that, today.
    //
    // Deliberately NOT saved. The stored Good through Date is what marks a loan as having an ACTIVE
    // payoff, and every transaction screen warns on it - so persisting a default would make every
    // loan somebody merely opened this screen for start warning on payments, charges and ledger
    // entries. It saves when the servicer actually sets a date, exactly as before.
    if (!newInputs.payoffDate.value) {
      const openRequest = (logged || []).find((s) => s.requestedBy && !s.requestFulfilled && s.payoffDate)
      newInputs.payoffDate.value = openRequest?.payoffDate || lastQuotedDate(logged) || formatDateYMD()
    }

    setInputs(newInputs)
    setLoading(false)
  }

  const loadStatements = async (): Promise<PayoffStatementLog[]> => {
    try {
      const res = await getPayoffStatements()
      const rows: PayoffStatementLog[] = res.statements || []
      setStatements(rows)
      return rows
    } catch {
      // The log is a record of what already went out; failing to read it must not block the page.
      return []
    }
  }

  // Recompute the payoff line items whenever the Good through Date changes.
  useEffect(() => {
    if (isShared) return
    const loadLineItems = async () => {
      if (!payoffDate) {
        setLineItems([])
        setAvailable([])
        return
      }

      setLoading(true)
      try {
        const res = await getPayoffLineItems({
          payoffDate,
          ...(isPartial
            ? {
                variant: PARTIAL,
                collateralId,
                // Sent only once the servicer has typed something, so an untouched field lets the
                // backend quote the register's release price rather than being overridden with 0.
                ...(quotedPrincipal === '' ? {} : { releasePrincipal: removeComma(quotedPrincipal) }),
              }
            : {}),
        })
        const raw: BackendLine[] = res.lines || []
        const lastIdx = raw.length - 1

        const shown: LineItem[] = []
        const addable: { title: string; value: string }[] = []

        raw.forEach((item, index) => {
          if (index === lastIdx) return // total line is derived below

          if (item.kind === 'computed') {
            // Show computed lines that have a value; anything that computes to $0 is hidden
            // and also kept out of the "add categories" picker.
            if (toNumber(item.value) !== 0) {
              shown.push({
                title: item.title,
                value: displayValue(item.value),
                optional: item.optional,
                // Optional lines (impounds credit) are opt-in so they don't silently move the balance.
                included: !item.optional,
              })
            }
          } else {
            // Manual lines have no computed value — offer them in the picker to add and fill in.
            addable.push({ title: item.title, value: '0' })
          }
        })

        setLineItems(shown)
        setAvailable(addable)
        // The backend resolves the release price; reflecting it back keeps the field showing what
        // is actually being quoted instead of an empty box over a priced line.
        if (isPartial && quotedPrincipal === '' && res.partialRelease?.releasePrincipal) {
          setReleasePrincipal(String(res.partialRelease.releasePrincipal))
          setQuotedPrincipal(String(res.partialRelease.releasePrincipal))
        }
      } catch (error) {
        console.error('Failed to load line items:', error)
      } finally {
        setLoading(false)
      }
    }
    loadLineItems()
  }, [payoffDate, isShared, isPartial, collateralId, quotedPrincipal])

  if (isShared) {
    const balances = (shared.pageData || {}) as Record<string, number>
    const rows = [
      ['Current UPB', balances.currentUpb],
      ['Interest Reserve Balance', balances.interestReserveBalance],
      ['Construction Reserve Balance', balances.constructionReserveBalance],
      // One row per held bucket, matching the credit lines on the demand itself. The shared view
      // is what an outside party reads, so a single pooled "Trust Balance" left them unable to
      // tell which of the borrower's buckets the credit came out of.
      ['Escrow Repair Holdback', balances.escrowRepairHoldbackBalance],
      ['Impound Balance', balances.impoundBalance],
      ['Suspense Balance', balances.suspenseBalance],
      ['Trust Balance', balances.trustBalance],
    ]
    return (
      <div className="TrustLedger-container">
        <div className="max-w-screen-2xl m-auto">
          <h2 className="text-2xl font-bold flex items-center mb-5">Payoff</h2>
          <p className="text-sm text-gray-500 mb-4">
            Shared read-only view shows payoff balances. PDF generation and line-item editing are not available in the
            shared view.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rows.map(([label, value]) => (
              <div key={String(label)} className="border rounded-lg p-4 bg-gray-50">
                <div className="text-sm text-gray-600">{label}</div>
                <div className="text-xl font-semibold">${getPrice2decimal(Number(value) || 0)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)

    newInputs[key].value = value
    newInputs[key].error = InputValidate(newInputs[key])

    setInputs(newInputs)
    setFieldValueChanged(true)
  }

  const onUpdate = async (key: string) => {
    if (!fieldValueChanged) return
    setFieldValueChanged(false)
    const input: any = inputs[key]
    if (input.error && input.error.length > 0) return
    setLoading(true)
    let value = inputs[key].value
    if (key === 'payoffAmount') value = removeComma(value)
    const reqBody = {
      [key]: value,
    }
    await updateTrustLedgerAdditional(reqBody)
    setLoading(false)
  }

  const onGenInputChange = (key: string, value: any) => {
    let newInputs = cloneDeep(genInputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setGenInputs(newInputs)
  }

  const onAdditionalShowHistory = (key: string) => {
    const options = {
      table: 'TrustLedger',
      field: key,
      keys: {
        field: key,
        loanNumber,
      },
    }
    openAuditLog(options)
  }

  // Borrower requests nobody has answered yet. Surfaced above the form, because the whole point of
  // a request is that the servicer sees it when they open the screen.
  const openRequests = statements.filter((s) => s.requestedBy && !s.requestFulfilled)

  const total = lineItems.reduce((sum, item) => (item.included ? sum + toNumber(item.value) : sum), 0)

  const onLineItemChange = (index: number, value: any) => {
    setLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, value } : item)))
  }

  const onLineItemTitleChange = (index: number, title: string) => {
    setLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, title } : item)))
  }

  const toggleIncluded = (index: number) => {
    setLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, included: !item.included } : item)))
  }

  const removeLineItem = (index: number) => {
    const item = lineItems[index]
    setLineItems((prev) => prev.filter((_, i) => i !== index))
    // A predefined category returns to the picker so it can be added again.
    if (item?.isCategory && item.title) {
      setAvailable((prev) =>
        prev.some((c) => c.title === item.title) ? prev : [...prev, { title: item.title, value: '0' }],
      )
    }
  }

  const addCategory = (title: string) => {
    const category = available.find((c) => c.title === title)
    if (!category) return
    setAvailable((prev) => prev.filter((c) => c.title !== title))
    setLineItems((prev) => [
      ...prev,
      { title: category.title, value: displayValue(category.value), included: true, isCategory: true },
    ])
  }

  const addCustomLine = () => {
    setLineItems((prev) => [...prev, { title: 'New Line Item', value: '0', included: true, isCategory: false }])
  }

  const onGenerate = async (draft = draftOnly) => {
    if (missingTerms.length) {
      toast(`${missingTerms.join(' and ')} must be filled on Servicing Information first`, { type: 'error' })
      return
    }
    if (!payoffDate) {
      toast('Good through Date should be filled', { type: 'error' })
      return
    }
    if (isPartial && !collateralId) {
      toast('Choose which collateral is paying off', { type: 'error' })
      return
    }
    if (isCombined && !combinedLoans.length) {
      toast('Choose at least one related loan to combine into this payoff', { type: 'error' })
      return
    }

    let hasError = false
    let newInputs = cloneDeep(genInputs)

    const data: Record<string, any> = { payoffDate, draft }
    if (isCombined) data.loanNumbers = combinedLoans
    if (isPartial) {
      data.variant = PARTIAL
      data.collateralId = collateralId
      data.recastPayment = recast
      if (releasePrincipal !== '') data.releasePrincipal = removeComma(releasePrincipal)
    }
    // A draft is never emailed out: it is a working figure, not a demand.
    const sendEmail = !draft && genInputs.sendEmail?.value === 'email'

    for (const key in newInputs) {
      // Not emailing means no recipient, not an unvalidated one: the field is prefilled with the
      // borrower's address, and sending it along is what used to email them a Download PDF.
      if (key === 'emailTo' && !sendEmail) {
        data[key] = ''
        continue
      }

      newInputs[key].error = InputValidate(newInputs[key])
      data[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }

    // Additional validation for email field when sending email
    if (sendEmail && !data.emailTo) {
      newInputs.emailTo.error = 'Email is required when sending email'
      hasError = true
    }

    setGenInputs(newInputs)
    if (hasError) return

    // Only included lines go on the statement; blanks become $0. The last line is the total
    // (the backend re-sums and formats it).
    const lines: [string, string][] = lineItems
      .filter((item) => item.included)
      .map((item) => [item.title || '', displayValue(item.value)])
    lines.push([totalTitle, total.toString()])
    data.lines = lines

    setLoading(true)
    try {
      // The lines above are this loan's, and the combined endpoint quotes the other loans itself -
      // so the same payload serves both, and a combined demand cannot disagree with the figures
      // the servicer is looking at.
      const pdfData = isCombined ? await downloadCombinedPayoff(data) : await downloadPayoff(data)

      if (!sendEmail) {
        // Named for the document it is: a partial release filed or forwarded as "Payoff Statement"
        // is how the wrong instrument gets recorded.
        const label = isCombined
          ? 'Combined Payoff Statement'
          : isPartial
          ? 'Partial Release Demand'
          : 'Payoff Statement'
        downloadFile(draft ? `${label} - DRAFT.pdf` : `${label}.pdf`, pdfData)
      }
      // Everything generated is logged, so pick the new row up straight away.
      await loadStatements()
    } finally {
      setLoading(false)
    }
  }

  /** Load the borrower's requested good-through date into the form, ready to quote it for real. */
  const onUseRequestedDate = (request: PayoffStatementLog) => {
    if (!request.payoffDate) return
    let newInputs = cloneDeep(inputs)
    newInputs.payoffDate.value = request.payoffDate
    setInputs(newInputs)
    setFieldValueChanged(true)
    updateTrustLedgerAdditional({ payoffDate: request.payoffDate })
  }

  const onDownloadStatement = async (item: PayoffStatementLog) => {
    setLoading(true)
    try {
      const pdfData = await downloadPayoffStatement(item.id)
      downloadFile(item.fileName || 'Payoff Statement.pdf', pdfData)
    } finally {
      setLoading(false)
    }
  }

  const onApproveStatement = async (item: PayoffStatementLog) => {
    const confirmed = await confirm(
      `Approve the payoff statement good through ${item.payoffDate || '-'} for ${formatUSD(
        Number(item.totalAmount) || 0,
      )}? Approving stamps your name and the time on it and files the PDF into the loan's documents.`,
    )
    if (!confirmed) return
    setLoading(true)
    try {
      const res = await approvePayoffStatement(item.id)
      if (!res?.success) {
        toast(res?.message || 'Could not approve the statement.', { type: 'error' })
        return
      }
      toast('Payoff statement approved and filed into the loan documents.', { type: 'success' })
      await loadStatements()
    } finally {
      setLoading(false)
    }
  }

  /**
   * The closing funded: post the money, release the lien, recast if the demand asked for it.
   *
   * Its own action rather than part of approving, because approving issues the demand and the
   * funds arrive days later. Releasing under the configured release price comes back as
   * `needsConfirmation` with the reason, and is only re-sent once the servicer has agreed to it -
   * that is the decision that quietly thins the remaining security.
   */
  const onRecordRelease = async (item: PayoffStatementLog) => {
    const parcel = collateral.find((row) => row.id === item.collateralId)
    const confirmed = await confirm(
      `Record ${formatUSD(Number(item.totalAmount) || 0)} received against this partial release${
        parcel?.address ? ` of ${parcel.address}` : ''
      }? This posts the payment, releases that lien and leaves the loan open on its remaining collateral.`,
    )
    if (!confirmed) return

    setLoading(true)
    try {
      const body: Record<string, any> = { receivedDate: formatDateYMD() }
      let res = await recordPartialRelease(item.id, body)

      if (res?.needsConfirmation) {
        const proceed = await confirm(
          `${(res.warnings || []).join(' ')} Record the release anyway?`,
        )
        if (!proceed) return
        res = await recordPartialRelease(item.id, { ...body, acceptWarnings: true })
      }

      if (!res?.success) {
        toast(res?.message || 'Could not record the release.', { type: 'error' })
        return
      }

      const parts = [
        `Partial payoff posted. Remaining principal balance ${formatUSD(Number(res.remainingUpb) || 0)}.`,
        res.recast ? `Payment recast to ${formatUSD(Number(res.recast.current) || 0)}.` : '',
        // Said out loud rather than swallowed: the servicer asked for a recast and did not get one.
        res.recastSkipped ? 'The payment could not be recast, so the existing payment is unchanged.' : '',
      ].filter(Boolean)
      toast(parts.join(' '), { type: res.recastSkipped ? 'warning' : 'success' })

      await loadStatements()
      setCollateralVersion((prev) => prev + 1)
    } finally {
      setLoading(false)
    }
  }

  const onDeleteStatement = async (item: PayoffStatementLog) => {
    const confirmed = await confirm('Remove this statement from the log? The generated PDF is deleted with it.')
    if (!confirmed) return
    setLoading(true)
    try {
      const res = await deletePayoffStatement(item.id)
      if (!res?.success) toast(res?.message || 'Could not remove the statement.', { type: 'error' })
      await loadStatements()
    } finally {
      setLoading(false)
    }
  }

  const sendEmail = genInputs.sendEmail?.value === 'email'

  return (
    <div className="TrustLedger-container">
      <div className="max-w-screen-2xl m-auto">
        <div className="relative bg-white">
          <h2 className="text-2xl font-bold flex items-center mb-5">Payoff</h2>
          <LayoutLoading show={loading} />
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            <div className="xl:col-span-3">
              {openRequests.map((request) => (
                <div
                  key={request.id}
                  className="mb-4 rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900"
                >
                  <div className="font-semibold">Payoff statement requested</div>
                  <div className="mt-1">
                    {request.requestedBy} asked for a payoff good through{' '}
                    <span className="font-semibold">{request.payoffDate || '-'}</span>
                    {request.requestedAt ? ` on ${formatTime(request.requestedAt)}` : ''}.
                  </div>
                  {request.requestNote && <div className="mt-1 italic">&ldquo;{request.requestNote}&rdquo;</div>}
                  <div className="mt-1 text-xs">
                    Open it below, adjust the figures if you need to, then generate and approve. The approved statement
                    is published back to the borrower&apos;s portal.
                  </div>
                  <div className="mt-2">
                    <Button link color="shade-blue" onClick={() => onUseRequestedDate(request)}>
                      Open this request
                    </Button>
                  </div>
                </div>
              ))}

              {/* A blanket loan's parcels and release prices, where the partial payoff needs them. */}
              <CollateralPanel
                canEdit={!draftOnly}
                reloadKey={collateralVersion}
                onLoaded={(rows) => {
                  setCollateral(rows)
                  // Preselect when there is only one parcel left to release: on a loan down to its
                  // last two, picking from a list of one is just a click in the way.
                  const releasable = rows.filter((row) => !`${row.releasedDate || ''}`.trim())
                  setCollateralId((prev) =>
                    prev && releasable.some((row) => row.id === prev) ? prev : releasable.length === 1 ? releasable[0].id : prev,
                  )
                }}
              />

              {/* Full payoff retires the note; a partial pays off ONE parcel and leaves the loan
                  running on the rest; a combined one retires this loan and its related loans on a
                  single wire. They are different documents and different transactions, so the
                  choice is made before anything is quoted rather than at the end. */}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600 mr-1">This payoff is:</span>
                {[
                  { key: 'full', label: 'Full payoff' },
                  { key: 'partial', label: 'Partial payoff (release one collateral)' },
                  // Offered only to a user holding the full payoff right: a combined demand reads
                  // across loan files, and the endpoints behind it refuse the draft-only grant a
                  // lender gives its brokers.
                  ...(draftOnly ? [] : [{ key: 'combined', label: 'Combined payoff (related loans)' }]),
                ].map((option) => {
                  const active = option.key === (isCombined ? 'combined' : isPartial ? 'partial' : 'full')
                  return (
                    <button
                      key={option.key}
                      type="button"
                      className={`px-3 py-1.5 text-sm rounded border transition ${
                        active
                          ? 'bg-shade-blue text-white border-shade-blue'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-shade-blue'
                      }`}
                      onClick={() => {
                        if (active) return
                        setIsPartial(option.key === 'partial')
                        setIsCombined(option.key === 'combined')
                        // The quoted principal belongs to the mode that quoted it; switching clears
                        // it so the new mode prices itself rather than inheriting a stale figure.
                        setReleasePrincipal('')
                        setQuotedPrincipal('')
                        // Loans chosen for a combined demand belong to that demand and nothing else.
                        if (option.key !== 'combined') setCombinedLoans([])
                      }}
                    >
                      {option.label}
                    </button>
                  )
                })}
              </div>

              {isCombined && (
                <CombinedPayoffPanel
                  payoffDate={String(payoffDate || '')}
                  thisLoanDisplayNo={String(loanNumber)}
                  thisLoanTotal={total}
                  selected={combinedLoans}
                  onSelectedChange={setCombinedLoans}
                />
              )}

              {isPartial && (
                <div className="mb-4 rounded border border-gray-200 bg-gray-50 px-4 py-3">
                  {releasableCollateral.length === 0 ? (
                    <div className="text-sm text-gray-600">
                      No collateral on this loan is available to release. Add the properties securing it under
                      Collateral above.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Collateral paying off</label>
                        <select
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                          value={collateralId ?? ''}
                          onChange={(e) => {
                            setCollateralId(e.target.value ? Number(e.target.value) : null)
                            // A different parcel has a different release price; let it quote its own.
                            setReleasePrincipal('')
                            setQuotedPrincipal('')
                          }}
                        >
                          <option value="">Choose a property…</option>
                          {releasableCollateral.map((row) => (
                            <option key={row.id} value={row.id}>
                              {row.address || `Collateral #${row.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Principal to release it</label>
                        <input
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm text-right"
                          value={releasePrincipal}
                          onChange={(e) => setReleasePrincipal(e.target.value)}
                          onBlur={() => setQuotedPrincipal(releasePrincipal)}
                        />
                        {selectedCollateral ? (
                          <div className="text-[11px] text-gray-500 mt-1">
                            {selectedCollateral.releasePrice
                              ? `Release price ${formatUSD(Number(selectedCollateral.releasePrice))}`
                              : 'No release price set for this collateral.'}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex items-start">
                        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer mt-5">
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={recast}
                            onChange={() => setRecast((prev) => !prev)}
                          />
                          <span>
                            Recast the payment
                            <span className="block text-[11px] text-gray-500">
                              Re-amortize over the remaining term on the reduced balance. Off, the payment stays as
                              it is and the loan retires sooner.
                            </span>
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {missingTerms.length > 0 && (
                <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {missingTerms.join(' and ')} {missingTerms.length === 1 ? 'is' : 'are'} not set on Servicing
                  Information. A payoff statement cannot be generated until{' '}
                  {missingTerms.length === 1 ? 'it is' : 'they are'} filled in.
                </div>
              )}
              <div className={`grid gap-4 md:grid-cols-4 grid-cols-1 mb-3`}>
                {Object.keys(inputs).map((key, index) => {
                  const input: any = cloneDeep(inputs[key])
                  if (input.visible === false) return null
                  return (
                    <div className={`input md:col-span-${input.span || 1}`} key={index}>
                      <RenderInput
                        input={input}
                        Key={key}
                        onChange={onChange}
                        onBlur={onUpdate}
                        showHistory={() => onAdditionalShowHistory(key)}
                      />
                    </div>
                  )
                })}
              </div>

              {/* A quote for any day but today is read as "what is owed" unless it says otherwise,
                  which is how a demand good through an earlier date got compared against a payment
                  keyed for it and the one per-diem between them read as a bug. */}
              {!!payoffDate && String(payoffDate) !== formatDateYMD() && (
                <p className="mb-4 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  Every figure below is accrued to {String(payoffDate)}, not to today. Interest and any charge
                  carrying a rate move with this date, so these are the amounts owed on that day. Change the Good
                  through Date above to re-quote.
                </p>
              )}

              {/* Download-or-email is a demand-issuing choice; a draft-only user has neither. */}
              <div
                className={`grid gap-4 md:grid-cols-4 grid-cols-1 mb-4 pb-4 border-b border-gray-200 ${
                  draftOnly ? 'hidden' : ''
                }`}
              >
                {Object.keys(genInputs).map((key) => {
                  const input = genInputs[key]
                  // Hide email field if not sending email
                  if (key === 'emailTo' && !sendEmail) return null
                  return (
                    <div className="input" key={key}>
                      <RenderInput input={input} Key={key} onChange={onGenInputChange} />
                    </div>
                  )
                })}
              </div>

              {/* Always on screen: the line items are the substance of this page, and hiding them
                  until a date is typed made an opened payoff look empty. With no date they say so
                  rather than silently showing nothing. */}
              <div className="grid grid-cols-1 gap-2 mb-4">
                {!payoffDate && (
                  <div className="rounded border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                    Enter a Good through Date above to calculate the payoff.
                  </div>
                )}
                <div className={payoffDate ? '' : 'hidden'}>
                  <div className="mb-3 flex justify-between items-center gap-3">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">
                        {isPartial ? 'Partial Release Line Items' : 'Payoff Line Items'}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Edit the values below as needed before generating the PDF.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {available.length > 0 && (
                        <select
                          className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700"
                          value=""
                          onChange={(e) => {
                            if (e.target.value) addCategory(e.target.value)
                          }}
                        >
                          <option value="">+ Add category…</option>
                          {available.map((c) => (
                            <option key={c.title} value={c.title}>
                              {c.title.trim()}
                            </option>
                          ))}
                        </select>
                      )}
                      <Button link onClick={addCustomLine} color="shade-blue">
                        + Custom line
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {lineItems.map((item, index) => {
                      const titleInput: InputType = {
                        inputType: 'text',
                        title: 'Line Item Description',
                        value: item.title || '',
                      }
                      const valueInput: InputType = {
                        inputType: 'text',
                        title: 'Amount',
                        value: item.value,
                        type: 'thousandSep',
                        prefix: '$',
                        disabled: item.optional ? !item.included : false,
                      }

                      return (
                        <div key={index} className="flex items-center gap-2">
                          <div className="flex-1 grid grid-cols-2 gap-3">
                            <div>
                              <RenderInput
                                input={titleInput}
                                Key={`${index}_title`}
                                onChange={(_: string, value: any) => onLineItemTitleChange(index, value)}
                              />
                            </div>
                            <div>
                              <RenderInput
                                input={valueInput}
                                Key={`${index}_value`}
                                onChange={(_: string, value: any) => onLineItemChange(index, value)}
                              />
                            </div>
                          </div>
                          {/* Fixed-width slot so every row's inputs line up whether or not it has a checkbox. */}
                          <div className="w-20 shrink-0 flex items-center">
                            {item.optional && (
                              <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap cursor-pointer">
                                <input type="checkbox" checked={item.included} onChange={() => toggleIncluded(index)} />
                                Include
                              </label>
                            )}
                          </div>
                          <span
                            className="w-6 shrink-0 flex justify-center text-red-500 hover:cursor-pointer hover-shadow1 rounded"
                            onClick={() => removeLineItem(index)}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </span>
                        </div>
                      )
                    })}

                    {/* Total */}
                    <div className="flex items-center gap-2 border-t border-gray-200 pt-2 mt-2">
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <div className="flex items-center font-semibold text-gray-800">{totalTitle}</div>
                        <div className="flex items-center justify-end font-semibold text-gray-900 text-lg pr-1">
                          {formatUSD(total)}
                        </div>
                      </div>
                      {/* Match the line rows' trailing slots (checkbox + trash) so the total aligns. */}
                      <div className="w-20 shrink-0" />
                      <span className="w-6 shrink-0" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center items-center gap-3 mt-4">
                {/* A draft quotes the same figures watermarked DRAFT. It books no fees and cannot be
                    approved, so it is safe to hand out while a payoff is still being negotiated. */}
                <Button
                  onClick={() => onGenerate(true)}
                  className="px-[28px]"
                  color={draftOnly ? 'shade-blue' : 'gray'}
                  disabled={missingTerms.length > 0}
                >
                  Generate Draft
                </Button>
                {!draftOnly && (
                  <Button onClick={() => onGenerate(false)} className="px-[36px]" disabled={missingTerms.length > 0}>
                    {sendEmail ? 'Generate & Email' : 'Generate & Download'}
                  </Button>
                )}
              </div>
            </div>

            {/* Side log: every statement generated on this loan, kept exactly as it printed. */}
            <div className="xl:col-span-1">
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 xl:sticky xl:top-4">
                <h3 className="font-semibold text-lg text-gray-800">
                  {draftOnly ? 'Generated Drafts' : 'Generated Statements'}
                </h3>
                <p className="text-sm text-gray-600 mt-1 mb-3">
                  Every payoff generated on this loan, as it was quoted. Approving one stamps your name and files the
                  PDF into the loan&apos;s documents.
                </p>
                {statements.length === 0 ? (
                  <div className="text-sm text-gray-500 italic">No payoff statements have been generated yet.</div>
                ) : (
                  <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                    {statements.map((item) => (
                      <div key={item.id} className="bg-white border border-gray-200 rounded p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-semibold text-gray-900">
                              {formatUSD(Number(item.totalAmount) || 0)}
                            </div>
                            <div className="text-xs text-gray-600">Good through {item.payoffDate || '-'}</div>
                          </div>
                          <span
                            className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${
                              item.status === 'approved'
                                ? 'bg-green-100 text-green-700'
                                : item.status === 'draft'
                                ? 'bg-slate-200 text-slate-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}
                          >
                            {item.status === 'approved'
                              ? 'Approved'
                              : item.status === 'draft'
                              ? item.requestedBy && !item.requestFulfilled
                                ? 'Requested'
                                : 'Draft'
                              : 'Pending approval'}
                          </span>
                        </div>
                        {item.variant === PARTIAL && (
                          <div className="mt-1 text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded px-2 py-1">
                            Partial release
                            {(() => {
                              const parcel = collateral.find((row) => row.id === item.collateralId)
                              return parcel?.address ? ` · ${parcel.address}` : ''
                            })()}
                            <div className="text-[11px]">
                              {formatUSD(Number(item.releasePrincipal) || 0)} to principal
                              {item.recastPayment ? ' · recast on posting' : ''}
                            </div>
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-2">
                          Generated {formatTime(item.createdAt)}
                          {item.createdBy ? ` by ${item.createdBy}` : ''}
                        </div>
                        {item.emailedTo && <div className="text-xs text-gray-500">Emailed to {item.emailedTo}</div>}
                        {item.requestedBy && (
                          <div className="text-xs text-blue-700 mt-1">
                            Requested by {item.requestedBy}
                            {item.requestFulfilled ? ' (answered)' : ''}
                          </div>
                        )}
                        {item.status === 'approved' && (
                          <div className="text-xs text-green-700 mt-1">
                            Approved {item.approvedAt ? formatTime(item.approvedAt) : ''}
                            {item.approvedBy ? ` by ${item.approvedBy}` : ''}
                            {item.requestedBy ? ' · shared to the borrower portal' : ''}
                          </div>
                        )}
                        {/* An approved partial release is a promise to release, not a released
                            lien: it says so until the funds are recorded. */}
                        {item.variant === PARTIAL && item.status === 'approved' && (
                          <div className={`text-xs mt-1 ${item.releaseRecordedAt ? 'text-green-700' : 'text-amber-700'}`}>
                            {item.releaseRecordedAt
                              ? `Funds recorded ${formatTime(item.releaseRecordedAt)} · lien released`
                              : 'Awaiting funds · the lien is not released yet'}
                          </div>
                        )}
                        <div className="flex items-center gap-3 mt-2">
                          <span
                            className="flex items-center gap-1 text-xs text-shade-blue hover:cursor-pointer"
                            onClick={() => onDownloadStatement(item)}
                          >
                            <ArrowDownTrayIcon className="w-4 h-4" />
                            Download
                          </span>
                          {item.status === 'pending' && canApprove && (
                            <span
                              className="flex items-center gap-1 text-xs text-green-700 hover:cursor-pointer"
                              onClick={() => onApproveStatement(item)}
                            >
                              <CheckCircleIcon className="w-4 h-4" />
                              Approve
                            </span>
                          )}
                          {item.variant === PARTIAL &&
                            item.status === 'approved' &&
                            !item.releaseRecordedAt &&
                            canApprove && (
                              <span
                                className="flex items-center gap-1 text-xs text-purple-800 hover:cursor-pointer"
                                onClick={() => onRecordRelease(item)}
                              >
                                <BanknotesIcon className="w-4 h-4" />
                                Record funds
                              </span>
                            )}
                          {item.status !== 'approved' && (
                            <span
                              className="flex items-center gap-1 text-xs text-red-500 hover:cursor-pointer ml-auto"
                              onClick={() => onDeleteStatement(item)}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
